import React, { Fragment, type RefObject, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import { SpotlightTarget } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { Flex } from '@atlassian/eoc-flex';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import { RuleCard, useMonitorRuleCards } from '@atlassian/eoc-rule-card';
import { Section } from '@atlassian/eoc-section';
import { ExperienceSuccess, useUFOComponentExperience } from '@atlassian/ufo';

import { SPOTLIGHT_NAMES } from '../../common/constants';
import { routingRulesLoadExperience } from '../../common/constants/performance-metrics';
import { type RoutingRuleProps } from '../../common/types';
import { useRoutingRulesController } from '../../controllers/routing-rules';
import { routingRulesResource } from '../../services/resources';

import { DeleteModal } from './delete-modal';
import { ElseCardContent } from './else-card-content';
import messages from './messages';
import { RoutingRuleModal } from './routing-rule-modal';
import { RuleCardActions } from './rule-card-actions';
import { RuleCardContent } from './rule-card-content';
import { RuleCardContainer, SectionContent } from './styled';

export default function RoutingRules({ onRoutingRuleModalOpen, schedules }: RoutingRuleProps) {
	useUFOComponentExperience(routingRulesLoadExperience);

	const { data } = useResource(routingRulesResource);
	const { formatMessage } = useIntl();

	const createRuleButtonRef = useRef<HTMLButtonElement>(null);

	const ruleActionButtonRefs = useRef<{
		[key: string]: RefObject<HTMLButtonElement> | null | undefined;
	}>({});

	const [returnFocusRef, setReturnFocusRef] = useState<RefObject<HTMLButtonElement> | undefined>(
		undefined,
	);

	assert(
		!!data,
		new AssertionError('RoutingRules', 'data should be fetched if there is no loading and error'),
	);

	const {
		routingRules: { entities, collapsed, hasEditRight, hasDeleteRight },
		routingRuleConditionFields,
		defaultTimeZone,
	} = data;

	const {
		closeRoutingRuleModal,
		openRoutingRuleModal,
		closeDeleteModal,
		handleEditClick,
		handleCloneClick,
		handleSubmitRule,
		handleDeleteClick,
		handleDeleteRule,
		handleChangeOrder,
		toggleSection,

		isDeleteModalOpen,
		isRoutingRuleModalOpen,
		isCloning,
		isCreating,
		isUpdating,
		isDeleting,
		deletingId,
		routeToOptions,
		editingData,
	} = useRoutingRulesController({ schedules });

	useMonitorRuleCards({
		droppableId: 'routing-rules',
		data: entities,
		onComplete: ({ updatedValue, targetIndex, sourceId }) => {
			handleChangeOrder({ id: sourceId, targetIndex }, updatedValue);
		},
	});

	return (
		<>
			<ExperienceSuccess experience={routingRulesLoadExperience} />
			<Section
				isOpen={!collapsed}
				onClick={toggleSection}
				title={
					<Flex columnGap={1} alignItems="center">
						<Heading as="h2" size="medium">
							{formatMessage(messages.title)}
						</Heading>
						{data.routingRules.entities.length > 0 && (
							<Badge max={300}>
								<span aria-hidden={true}>{data.routingRules.entities.length}</span>
							</Badge>
						)}
					</Flex>
				}
				actions={
					hasEditRight ? (
						<Tooltip content={formatMessage(messages.add)}>
							<IconButton
								label={formatMessage(messages.add)}
								onClick={(e) => {
									setReturnFocusRef(createRuleButtonRef);

									e.stopPropagation();
									openRoutingRuleModal();
								}}
								appearance="subtle"
								icon={EditorAddIcon}
								ref={createRuleButtonRef}
							/>
						</Tooltip>
					) : null
				}
			>
				<SectionContent>
					{entities.length > 0 &&
						entities.map((entity, i) => {
							// Backend always returns a routing rule thats why we need to check it
							// and show the `else` card instead of it.
							const isLastItem = i === entities.length - 1;
							const Wrapper = isLastItem ? SpotlightTarget : Fragment;
							let spotlightProps = {} as { name: string };

							if (isLastItem) {
								spotlightProps = {
									name: SPOTLIGHT_NAMES.lastEntity,
								};
							}

							return (
								<Wrapper key={i} {...spotlightProps}>
									<RuleCardContainer isLastItem={isLastItem}>
										<RuleCard
											key={entity.id}
											value={entity.id}
											droppableId={isLastItem ? '' : 'routing-rules'}
											title={isLastItem ? '' : entity.name}
											headingAs="h3"
											actions={
												<RuleCardActions
													isDeleted={!!entity.deleted}
													isLoading={isDeleting && deletingId === entity.id}
													isLastItem={isLastItem}
													onClone={() => {
														setReturnFocusRef(
															ruleActionButtonRefs?.current[entity.id] || undefined,
														);
														return handleCloneClick(entity);
													}}
													onEdit={() => {
														setReturnFocusRef(
															ruleActionButtonRefs?.current[entity.id] || undefined,
														);
														return handleEditClick(entity);
													}}
													onDelete={() => {
														setReturnFocusRef(
															ruleActionButtonRefs?.current[entity.id] || undefined,
														);
														return handleDeleteClick(entity);
													}}
													name={entity.name}
													hasEditRight={hasEditRight}
													hasDeleteRight={hasDeleteRight}
													ref={(el) => {
														ruleActionButtonRefs.current[entity.id] = el?.getActionButtonRef();
													}}
												/>
											}
										>
											{isLastItem ? (
												<ElseCardContent
													isDeleted={!!entity.deleted}
													routeToOptions={routeToOptions}
													entity={entity}
													entityCount={entities.length}
													hasEditRight={hasEditRight}
												/>
											) : (
												<RuleCardContent entity={entity} index={i} />
											)}
										</RuleCard>
									</RuleCardContainer>
								</Wrapper>
							);
						})}
				</SectionContent>
			</Section>
			<RoutingRuleModal
				isOpen={isRoutingRuleModalOpen}
				isCloning={isCloning}
				isLoading={isCreating || isUpdating}
				onSubmit={handleSubmitRule}
				onClose={closeRoutingRuleModal}
				conditionFields={routingRuleConditionFields}
				routeToOptions={routeToOptions}
				data={editingData}
				defaultTimeZone={defaultTimeZone}
				onOpen={onRoutingRuleModalOpen}
				returnFocusRef={returnFocusRef}
			/>
			<DeleteModal
				isOpen={isDeleteModalOpen}
				onClose={closeDeleteModal}
				onConfirm={handleDeleteRule}
				name={editingData?.name || ''}
				returnFocusRef={returnFocusRef}
			/>
		</>
	);
}
